import { GoogleMap, Marker, MarkerClusterer } from '@react-google-maps/api';
import { first, get } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';

import { mapStyles, markerShape } from './MapWithControlledZoom.constants';
import useMapWithControlledZoom from './MapWithControlledZoom.hooks';
import { $GoogleMap } from './MapWithControlledZoom.styles';
import type { AdaptedPickupPoint } from './MapWithControlledZoom.types';

const HOME_MARKER_URL = `${
  import.meta.env.VITE_STATIC_HASH
}/images/home_marker.svg`;

interface PickupPoint {
  latitude: number;
  longitude: number;
  id?: string;
  name?: string;
  carrierBrand: string;
  chain: string;
}

interface SearchAddress {
  location: {
    lat: number;
    lng: number;
  };
}

interface Bounds {
  north: number;
  east: number;
  south: number;
  west: number;
}

interface MapWithControlledZoomProps {
  onSelectPickupPoint: (pickupPoint: PickupPoint) => void;
  pickupPoints: PickupPoint[];
  searchAddress: SearchAddress;
  selectedPickupPointId?: string | null;
  bounds: Bounds;
  center: { lat: number; lng: number };
}

const MapWithControlledZoom: React.FC<MapWithControlledZoomProps> = ({
  onSelectPickupPoint,
  pickupPoints,
  searchAddress,
  selectedPickupPointId,
  bounds,
  center,
}) => {
  const [hasHumanZoom, setHasHumanZoom] = useState(false);
  const [zoom, setZoom] = useState(15);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const sellerId = useSelector((state: any) => {
    const { items } = state.orders;

    return get(first(items), 'sellerId', null);
  });
  const { isLoading, adaptedPickupPoints } = useMapWithControlledZoom({
    pickupPoints: pickupPoints as any,
    sellerId: sellerId || '',
  });

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onZoomChanged = () => {
    if (map) {
      const currentZoom = map.getZoom();

      if (currentZoom !== undefined) {
        setZoom(currentZoom);
      }
      setHasHumanZoom(true);
    }
  };

  const handleSelectPickupPoint = (pickupPoint: AdaptedPickupPoint) => () => {
    const lat =
      typeof pickupPoint.latitude === 'number'
        ? pickupPoint.latitude
        : (pickupPoint.latitude as any)?.lat?.();
    const lng =
      typeof pickupPoint.longitude === 'number'
        ? pickupPoint.longitude
        : (pickupPoint.longitude as any)?.lng?.();

    if (map && lat !== undefined && lng !== undefined) {
      map.panTo({ lat, lng });
      setZoom(17);
    }

    onSelectPickupPoint(pickupPoint as any);
  };

  const autoFocus = useCallback(() => {
    const firstPoint = adaptedPickupPoints[0];
    const lat =
      typeof firstPoint?.latitude === 'number'
        ? firstPoint.latitude
        : (firstPoint?.latitude as any)?.lat?.();
    const lng =
      typeof firstPoint?.longitude === 'number'
        ? firstPoint.longitude
        : (firstPoint?.longitude as any)?.lng?.();

    if (map && lat !== undefined && lng !== undefined) {
      const x = map.getBounds();

      if (x) {
        const northEast = x.getNorthEast();
        const southWest = x.getSouthWest();

        if (
          !(
            (lat < northEast.lat() && lat > southWest.lat()) ||
            (lng < northEast.lng() && lng > southWest.lng())
          )
        ) {
          setZoom(zoom - 1);
        }
      }
    }
  }, [adaptedPickupPoints, zoom, map]);

  useEffect(() => {
    if (
      !hasHumanZoom &&
      adaptedPickupPoints &&
      adaptedPickupPoints?.length > 0
    ) {
      autoFocus();
    }
  }, [autoFocus, adaptedPickupPoints, zoom, hasHumanZoom]);

  if (isLoading) {
    return <BaseLoader />;
  }

  if (!bounds) {
    return null;
  }

  return (
    <$GoogleMap>
      <GoogleMap
        center={center}
        zoom={zoom}
        onZoomChanged={onZoomChanged}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{ styles: mapStyles as any }}
      >
        <MarkerClusterer
          averageCenter
          enableRetinaIcons
          gridSize={500 / Math.max(zoom, 1)}
          zoomOnClick={false}
          maxZoom={15}
        >
          {(clusterer) => (
            <>
              {adaptedPickupPoints?.map(
                (adaptedPickupPoint: AdaptedPickupPoint) => {
                  const isSelected =
                    selectedPickupPointId === (adaptedPickupPoint as any).id;
                  const zIndex = isSelected ? 1000 : 1;

                  const lat =
                    typeof adaptedPickupPoint.latitude === 'number'
                      ? adaptedPickupPoint.latitude
                      : (adaptedPickupPoint.latitude as any)?.lat?.();
                  const lng =
                    typeof adaptedPickupPoint.longitude === 'number'
                      ? adaptedPickupPoint.longitude
                      : (adaptedPickupPoint.longitude as any)?.lng?.();

                  if (lat === undefined || lng === undefined) {
                    return null;
                  }

                  return (
                    <Marker
                      key={(adaptedPickupPoint as any).id}
                      position={{
                        lat,
                        lng,
                      }}
                      onClick={handleSelectPickupPoint(adaptedPickupPoint)}
                      title={(adaptedPickupPoint as any).name}
                      zIndex={zIndex}
                      clickable
                      shape={markerShape as any}
                      icon={
                        isSelected
                          ? adaptedPickupPoint.selectedIcon
                          : adaptedPickupPoint.icon
                      }
                      clusterer={clusterer}
                    />
                  );
                },
              )}
            </>
          )}
        </MarkerClusterer>
        <Marker
          position={{
            lat: searchAddress.location.lat,
            lng: searchAddress.location.lng,
          }}
          icon={HOME_MARKER_URL}
        />
      </GoogleMap>
    </$GoogleMap>
  );
};

MapWithControlledZoom.displayName = 'MapWithControlledZoom';

export default React.memo(MapWithControlledZoom);
